import { FC, useState } from "react";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@capacit-com/lara-law-shared-ui";
import { Button } from "@capacit-com/lara-law-shared-ui";
import { Checkbox } from "@capacit-com/lara-law-shared-ui";
import { WorkspaceDocument } from "@/models/workspace/Workspace";
import { FolderTree, FileText, User } from "lucide-react";
import { cn } from "@/lib/utils";
import { showSuccessToast } from "@/components/custom-toasts/toasts";

export type DeletedFileInfo = {
  document: WorkspaceDocument;
  folderPath: string;
  /** Optional display name of the document owner */
  ownerName?: string;
};

interface DeletedFilesDialogProps {
  isOpen: boolean;
  onClose: () => void;
  deletedFiles: DeletedFileInfo[];
  onDelete: (documentIds: string[]) => Promise<void>;
  isDeleting?: boolean;
}

const DeletedFilesDialog: FC<DeletedFilesDialogProps> = ({
  isOpen,
  onClose,
  deletedFiles,
  onDelete,
  isDeleting = false,
}) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    new Set(deletedFiles.map((f) => f.document.id))
  );

  const handleToggleSelect = (documentId: string) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(documentId)) {
        newSet.delete(documentId);
      } else {
        newSet.add(documentId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedIds.size === deletedFiles.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(deletedFiles.map((f) => f.document.id)));
    }
  };

  const handleDelete = async () => {
    if (selectedIds.size === 0) {
      onClose();
      return;
    }
    const count = selectedIds.size;
    await onDelete(Array.from(selectedIds));
    showSuccessToast(
      `${count} fil${count === 1 ? "" : "er"} slettet`,
      "Succes!"
    );
    setSelectedIds(new Set());
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>Filer ikke tilgængelige</DialogTitle>
          <DialogDescription>
            Følgende filer fra workspace kunne ikke tilgås i
            OneDrive/SharePoint. De kan enten være slettet, eller du har ikke
            adgang til dem med din nuværende konto. Hvis du ønsker at fjerne dem
            fra workspace, kan du slette dem herunder.
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          <div className="space-y-2">
            {/* Select All */}
            <div className="flex items-center space-x-2 p-2 border-b">
              <Checkbox
                id="select-all"
                checked={selectedIds.size === deletedFiles.length}
                onCheckedChange={handleSelectAll}
              />
              <label
                htmlFor="select-all"
                className="text-sm font-medium cursor-pointer"
              >
                Vælg alle ({selectedIds.size}/{deletedFiles.length})
              </label>
            </div>

            {/* File List */}
            <div className="space-y-1">
              {deletedFiles.map((fileInfo) => {
                const isSelected = selectedIds.has(fileInfo.document.id);
                return (
                  <div
                    key={fileInfo.document.id}
                    className={cn(
                      "flex items-start space-x-3 p-3 rounded-lg border transition-colors",
                      isSelected
                        ? "bg-primary/10 border-primary/25"
                        : "hover:bg-muted/50"
                    )}
                  >
                    <Checkbox
                      id={fileInfo.document.id}
                      checked={isSelected}
                      onCheckedChange={() =>
                        handleToggleSelect(fileInfo.document.id)
                      }
                      className="mt-1"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                        <label
                          htmlFor={fileInfo.document.id}
                          className="text-sm font-medium cursor-pointer truncate"
                        >
                          {fileInfo.document.name}
                        </label>
                      </div>
                      {fileInfo.folderPath && (
                        <div className="flex items-center space-x-2 mt-1 text-xs text-muted-foreground">
                          <FolderTree className="h-3 w-3 shrink-0" />
                          <span className="truncate">
                            {fileInfo.folderPath}
                          </span>
                        </div>
                      )}
                      {fileInfo.ownerName && (
                        <div className="flex items-center space-x-2 mt-1 text-xs text-muted-foreground">
                          <User className="h-3 w-3 shrink-0" />
                          <span className="truncate">
                            Ejer: {fileInfo.ownerName}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isDeleting}>
            Luk
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={selectedIds.size === 0 || isDeleting}
          >
            {isDeleting
              ? "Sletter..."
              : `Slet ${selectedIds.size} fil${
                  selectedIds.size === 1 ? "" : "er"
                }`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeletedFilesDialog;
